import * as React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';
import { LinearProgress, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useAppContext } from '../../contexts/AppContext';
import { NavigationMenuItem } from './NavigationMenuItem';
import Button from '@mui/material/Button';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grow from '@mui/material/Grow';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import ActionIcon from './icons/action';
import AdventureIcon from './icons/adventure';
import HyperCasualIcon from './icons/hypercasual';
import MultiplayerIcon from './icons/multiplayer';
import PuzzleIcon from './icons/puzzle';
import RacingIcon from './icons/racing';
import SportsIcon from './icons/sports';

interface NavigationMenuProps {
  sliceAmount?: number;
  size?: 'small' | 'medium' | 'large' | undefined;
}

interface ListProps {
  id: string;
  page?: string;
  code?: string;
  icons?: any;
  label: string;
  href?: string;
  width?: number;
  height?: number;
}

const NavigationMoreMenuItem = styled(NavigationMenuItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 2),
  color: theme.theme_component_navigation_menu_more_menu_item_color || theme.palette.text.primary,
})) as typeof NavigationMenuItem;

const NavigationMoreMenuPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.theme_component_navigation_bar_background_color || theme.palette.primary.main,
})) as typeof Paper;

const NavigationMoreMenuLabel = styled(Typography)(({ theme }) => ({
  whiteSpace: 'nowrap',
  textTransform: 'capitalize',
  color: theme.theme_component_navigation_menu_more_menu_button_label_color || theme.palette.primary.contrastText,
  fontSize: '16px'
})) as typeof Typography;

const NavigationMoreMenuButton = styled(Button)(({ theme }) => ({
  marginLeft: 'auto',
  color: theme.theme_component_navigation_menu_more_menu_button_down_icon_color || theme.palette.primary.contrastText,
})) as typeof Button;

const ArrowDropdownIconStyled = styled(ArrowDropDownIcon)(({ theme }) => ({
  marginLeft: 'auto',
  color: theme.theme_component_navigation_menu_more_menu_button_down_icon_color || theme.palette.primary.contrastText,
}));

export const NavigationMenu = (props: NavigationMenuProps) => {
  const { sliceAmount, size } = props;
  const theme = useTheme();
  const appContext = useAppContext();
  const { t } = useTranslation('navigation_menu_b');
  const { assetUrl } = appContext.useConfig();
  const { categories, isLoading } = appContext.useCategories();
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const [sliceIndex, setSliceIndex] = React.useState(sliceAmount);
  const belowLg = useMediaQuery(theme.breakpoints.down('lg'));
  const lg = useMediaQuery(theme.breakpoints.up('lg'));

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    // @ts-ignore
    if (event && anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  React.useEffect(() => {
    setSliceIndex(sliceAmount);

    return () => {
      setSliceIndex(10);
    };
  }, [sliceAmount]);

  return (
    <>
      {isLoading && <LinearProgress color="secondary" />}
      {categories &&
        categories
          .slice(0, sliceIndex)
          .map((item: any, index) => (
            <NavigationMenuItem
              key={index}
              title={item.name}
              href={item?.slug ? `/category/${item.slug}` : `${item.href}`}
              icon={item.icons}
              dense={true}
            />
          ))}
      {lg && categories && categories.slice(sliceIndex).length > 0 && (
        <>
          <NavigationMoreMenuButton
            ref={anchorRef}
            id="more-menu-button"
            aria-controls={open ? 'more-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            size={size}
            onClick={handleToggle}
          >
            <NavigationMoreMenuLabel variant="inherit">{t('button.more', 'More')}</NavigationMoreMenuLabel>
            <ArrowDropdownIconStyled />
          </NavigationMoreMenuButton>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            placement="bottom-end"
            transition
            disablePortal
            placeholder=""
            style={{
              zIndex: 1000,
              // minWidth: '100%',
            }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'right top' : 'right bottom',
                }}
              >
                <NavigationMoreMenuPaper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList
                      autoFocusItem={open}
                      id="more-menu"
                      aria-labelledby="more-menu-button"
                      onKeyDown={handleListKeyDown}
                    >
                      {categories &&
                        categories
                          .slice(sliceIndex)
                          .map((item: any, index) => (
                            <NavigationMoreMenuItem
                              key={index}
                              title={item.name}
                              href={`/collection/${item.code}`}
                              icon={item.icons}
                              dense={true}
                            />
                          ))}
                    </MenuList>
                  </ClickAwayListener>
                </NavigationMoreMenuPaper>
              </Grow>
            )}
          </Popper>
        </>
      )}
    </>
  );
};
